import { mapValues, values } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Subscription, from } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ProjectId } from 'api/models';
import { Task as ApiTask, TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { taskNormalizer } from 'data/tasks/schema';
import { logger } from 'services';
import { Field, FieldsStore, TaskField } from 'stores/fields';
import { loadedProjectTasks, loadingProjectTasks } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { toStoreTask } from 'utils/to-store-task';

const defaultFieldTranformer = (x) => x;

interface UseProjectTasksRefreshParams {
  projectId: ProjectId;
  fieldTransformer?: (field: Field) => TaskField;
}

export const useProjectTasksRefresh = ({
  projectId: _projectId,
  fieldTransformer = defaultFieldTranformer,
}: UseProjectTasksRefreshParams) => {
  const [, project] = _projectId;
  const dispatch = useDispatch();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const [initialized, setInitialized] = useState(false);

  const fields_lookup = useMemo(
    () =>
      mapValues(fieldsStore.byId, ([, x]) => fieldTransformer(x!)) as Record<
        number,
        TaskField
      >,
    [fieldTransformer, fieldsStore.byId],
  );

  useEffect(() => {
    if (
      initialized ||
      !FieldsStore.loaded(fieldsStore) ||
      FieldsStore.refreshing(fieldsStore)
    ) {
      return undefined;
    }

    const subscriptions: Subscription[] = [];

    subscriptions.push(
      from([ProjectId(project)])
        .pipe(
          tap((projectId) => dispatch(loadingProjectTasks(projectId))),
          mergeMap((projectId) =>
            from(TaskAPI.getTaskList(projectId[1])).pipe(
              map((tasks) => [projectId, tasks] as [ProjectId, ApiTask[]]),
            ),
          ),
        )
        .subscribe(
          ([projectId, apiTasks]) => {
            const updatedData = apiTasks.map((task) => ({
              ...task,
              fields: task.fields.map(withValidCustomFieldsForData),
            }));
            const {
              entities: { tasks },
            } = taskNormalizer(updatedData);
            const to_row = toStoreTask(fields_lookup, tasks);

            dispatch(
              loadedProjectTasks([projectId, values(tasks).map(to_row)]),
            );
          },
          (error) => {
            logger.error('failed to load all tasks', { error });
          },
          () => {
            setInitialized(true);
          },
        ),
    );

    return () => {
      logger.debug('unsubscribing from all tasks api handling');
      subscriptions.forEach((s) => s.unsubscribe());
    };
  }, [
    fieldsStore,
    fields_lookup,
    dispatch,
    project,
    initialized,
    setInitialized,
  ]);
};
